import { useRouter } from 'next/router';
import { t } from 'i18next';
import useLocales from '@/hooks/useLocales';
import { useModals } from '@mattjennings/react-modal-stack';
import { AlertDialog, ConfirmDialog } from '@/components/dialogs';
import { useSelector, useDispatch } from 'react-redux';
import useStepper from './checkout/useStepper';
import useErrorTracking from '@/hooks/userTracking/useErrorTracking';
import { resetSelectedAndClean } from '@/store/msci/msci.slice';

const errorCodes = [
  'LINK_INACTIVE',
  'LINK_EXPIRED',
  'LINK_USED',
  'LINK_NOT_AVAILABLE',
  'BOOKING_EXPIRED',
];

// generate-payment errors the user can recover from by reloading the page
// (BOL-256). PAYMENT_AMOUNT_MISMATCH additionally clears the selected MSI
// plan so summary and addon re-quote on a fresh payment plan (BOL-246).
const paymentRetryCodes = [
  'PAYMENT_AMOUNT_MISMATCH',
  'BOOKING_OXXO_AMOUNT_INVALID',
];

// The OXXO/cash paths fire confirmBackCode twice for the same error
// (useCallApiProcess fires it internally and useCheckoutPayBooking fires
// it again with the returned error), stacking two identical dialogs; the
// close icon only pops one layer, exposing the duplicate before the
// reload. Dedupe with a short time window instead of a boolean latch so
// a dismissal that never reaches the reload cb (programmatic closeModal,
// a throw before the modal opens) cannot suppress future retry modals.
let paymentRetryModalShownAt = 0;
const PAYMENT_RETRY_DEDUPE_MS = 2000;

export default function useDialogs() {
  const booking = useSelector((state) => state.booking);
  const { translate, getObjectOrMessage } = useLocales();
  const { openModal, closeModal } = useModals();

  const router = useRouter();

  const dispatch = useDispatch();

  const { jumpTo } = useStepper();

  const { checkoutErrorShown } = useErrorTracking();

  const getMessageObj = (code) => {
    let message = getObjectOrMessage(`messages.${code}`);
    if (message) {
      if (typeof message !== 'object') {
        if (message && typeof message === 'string') {
          const objbase = getObjectOrMessage(`messages.default`);
          message = {
            ...objbase,
            body: message,
          };
        } else {
          message = translate(`messages.default`);
        }
      }
    } else {
      const objbase = getObjectOrMessage(`messages.default`);
      message = {
        ...objbase,
        body: `${objbase.body}: (${code})`,
      };
    }

    return message;
  };

  const alert = (text, title = 'Lo sentimos.', acceptLabel = 'Entendido') => {
    openModal(AlertDialog, {
      title,
      text,
      acceptLabel,
    });
  };

  const alertLocale = (code) => {
    alert(translate(code));
  };

  const alertBackCode = (code) => {
    let message = getMessageObj(code);

    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    alert(message.body, message.title, message.button);
  };

  const confirmBackCode = (code, token) => {
    let message = getMessageObj(code);
    const path = router.pathname;

    if (paymentRetryCodes.includes(code) && path === '/payment') {
      const now = Date.now();
      if (now - paymentRetryModalShownAt < PAYMENT_RETRY_DEDUPE_MS) return;
      paymentRetryModalShownAt = now;

      const { title, text, acceptLabel } = t(`checkout.modals.${code}`, {
        returnObjects: true,
      });

      checkoutErrorShown({
        source: 'backend',
        code,
        message: text,
      });
      confirm({
        title,
        text,
        acceptLabel,
        token,
        cb: () => {
          if (code === 'PAYMENT_AMOUNT_MISMATCH') {
            dispatch(resetSelectedAndClean());
          }
          router.reload();
        },
      });
      return;
    }

    if (
      (errorCodes.includes(code) && path === '/payment') ||
      code === 'INVALID_SESSION_TOKEN'
    ) {
      const localesPath = `checkout.modals.${code}`;
      const { title, text, acceptLabel } = t(localesPath, {
        returnObjects: true,
      });

      checkoutErrorShown({
        source: 'backend',
        code,
        message: text,
      });
      confirm({
        title,
        text,
        acceptLabel,
        token,
        cb: () => {
          jumpTo('HOME');
        },
      });
      return;
    }
    checkoutErrorShown({
      source: 'backend',
      code,
      message: message.body,
    });
    confirm({
      text: message.body,
      title: message.title,
      acceptLabel: message.button,
      token,
    });
  };

  const confirm = ({
    title,
    text,
    acceptLabel,
    token,
    cb = () => {},
    omitBooking = false,
  }) => {
    openModal(ConfirmDialog, {
      title,
      text,
      acceptLabel,
      token,
      booking: booking?.orderInfo?.number,
      onConfirm: async () => {
        closeModal();
        cb();
      },
      onClose: cb,
      omitBooking,
    });
  };

  return {
    openModal,
    closeModal,
    alert,
    alertLocale,
    alertBackCode,
    confirmBackCode,
    confirm,
  };
}
